import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import {
  FaBars, FaTimes, FaChevronDown, FaChevronRight,
  FaServer, FaNetworkWired, FaLock, FaHeadset, FaTools, FaProjectDiagram,
  FaSatelliteDish, FaShieldAlt, FaGlobe, FaCogs, FaWifi, FaDesktop,
  FaMicrochip, FaPhoneAlt, FaRocket, FaSyncAlt, FaLayerGroup, FaHandshake,
  FaHardHat, FaUserShield, FaVideo
} from 'react-icons/fa';

const Navigation = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [servicesOpen, setServicesOpen] = useState(false);
  const [activeSubmenu, setActiveSubmenu] = useState(null);
  const [mobileServicesOpen, setMobileServicesOpen] = useState(false);
  const [mobileSubmenuOpen, setMobileSubmenuOpen] = useState(null);
  const [mobileCategoryOpen, setMobileCategoryOpen] = useState(null); // Track which category is expanded
  const location = useLocation();

  // BUILD & CONNECT - Infrastructure & Networking
  const buildConnectServices = [
    {
      title: 'Data Center Construction & Commissioning',
      description: 'L1–L5 commissioning, IST, RFS & TCO',
      icon: <FaHardHat size={18} />,
      link: '/services/data-center-commissioning',
      gradient: 'from-blue-600 to-cyan-500',
      bgColor: 'bg-blue-600'
    },
    {
      title: 'Data Center Excellence',
      description: 'World-class facility management',
      icon: <FaServer size={18} />,
      link: '/data-center',
      gradient: 'from-emerald-400 to-teal-600',
      bgColor: 'bg-emerald-500'
    },
    {
      title: 'Infrastructure Solutions',
      description: 'End-to-end infrastructure design',
      icon: <FaLayerGroup size={18} />,
      link: '/services/infrastructure',
      gradient: 'from-blue-500 to-indigo-600',
      bgColor: 'bg-blue-500'
    },
    {
      title: 'Network & Cabling',
      description: 'Structured cabling systems',
      icon: <FaNetworkWired size={18} />,
      link: '/services/network-cabling',
      gradient: 'from-cyan-400 to-blue-600',
      bgColor: 'bg-cyan-500'
    }
  ];

  // COMMUNICATE & CONNECT - Telecom Services
  const communicateServices = [
    {
      title: 'Telecom Solutions',
      description: 'Enterprise telecommunications',
      icon: <FaSatelliteDish size={18} />,
      link: '/services/telecom',
      gradient: 'from-violet-500 to-purple-700',
      bgColor: 'bg-violet-500'
    },
    {
      title: 'Telephony Systems',
      description: 'VoIP & unified communications',
      icon: <FaPhoneAlt size={18} />,
      link: '/services/telephony',
      gradient: 'from-pink-500 to-rose-600',
      bgColor: 'bg-pink-500'
    },
    {
      title: 'IoT Connectivity',
      description: 'Smart device integration',
      icon: <FaMicrochip size={18} />,
      link: '/services/iot',
      gradient: 'from-amber-400 to-orange-600',
      bgColor: 'bg-amber-500'
    }
  ];

  // PROTECT & SECURE - Security Services
  const protectServices = [
    {
      title: 'Data Center Security',
      description: 'End-to-end integrated security',
      icon: <FaVideo size={18} />,
      link: '/services/data-center-security',
      gradient: 'from-slate-600 to-blue-700',
      bgColor: 'bg-slate-600'
    },
    {
      title: 'Safety Professionals',
      description: 'Expert on-site safety staffing',
      icon: <FaUserShield size={18} />,
      link: '/services/safety-professionals',
      gradient: 'from-amber-500 to-orange-600',
      bgColor: 'bg-amber-500'
    },
    {
      title: 'PPE Solutions',
      description: 'Safety & compliance equipment',
      icon: <FaHardHat size={18} />,
      link: '/services/ppe-solutions',
      gradient: 'from-yellow-500 to-amber-600',
      bgColor: 'bg-yellow-500'
    },
    {
      title: 'Security Systems',
      description: 'Comprehensive security solutions',
      icon: <FaShieldAlt size={18} />,
      link: '/services/security',
      gradient: 'from-red-500 to-rose-700',
      bgColor: 'bg-red-500'
    },
    {
      title: 'Access Control',
      description: 'Smart access management',
      icon: <FaLock size={18} />,
      link: '/services/access-control',
      gradient: 'from-slate-600 to-gray-800',
      bgColor: 'bg-slate-600'
    }
  ];

  // DEPLOY & SCALE - Rollout Services (with submenu)
  const deployServices = [
    {
      title: 'National Rollouts',
      description: 'Coast-to-coast deployments',
      icon: <FaGlobe size={18} />,
      hasSubmenu: true,
      gradient: 'from-orange-500 to-red-600',
      bgColor: 'bg-orange-500',
      submenuId: 'rollouts',
      submenu: [
        { title: 'Network Refresh & Upgrade', description: 'Modernize your network', icon: <FaSyncAlt size={16} />, link: '/services/network-refresh', gradient: 'from-blue-400 to-cyan-500' },
        { title: 'WAP Deployment', description: 'Wireless access points', icon: <FaWifi size={16} />, link: '/services/wap-deployment', gradient: 'from-green-400 to-emerald-500' },
        { title: 'Hardware Deployment', description: 'Equipment installation', icon: <FaDesktop size={16} />, link: '/services/hardware-deployment', gradient: 'from-purple-400 to-violet-500' },
        { title: 'Site Surveys', description: 'Pre-deployment analysis', icon: <FaProjectDiagram size={16} />, link: '/services/site-surveys', gradient: 'from-amber-400 to-orange-500' }
      ]
    }
  ];

  // SUPPORT & MAINTAIN - IT Support Services
  const supportServices = [
    /* {
      title: 'Janitorial Staffing',
      description: 'Data center cleaning specialists',
      icon: <FaBroom size={18} />,
      link: '/services/janitorial-staffing',
      gradient: 'from-teal-600 to-cyan-500',
      bgColor: 'bg-teal-600'
    }, */
    {
      title: '24/7 IT Support',
      description: 'Round-the-clock assistance',
      icon: <FaHeadset size={18} />,
      link: '/services/it-support',
      gradient: 'from-green-500 to-emerald-600',
      bgColor: 'bg-green-500'
    },
    {
      title: 'IT Deployment',
      description: 'Seamless technology rollouts',
      icon: <FaRocket size={18} />,
      link: '/services/it-deployment',
      gradient: 'from-indigo-500 to-blue-700',
      bgColor: 'bg-indigo-500'
    },
    {
      title: 'Break & Fix',
      description: 'Rapid repair services',
      icon: <FaTools size={18} />,
      link: '/services/break-fix',
      gradient: 'from-yellow-500 to-amber-600',
      bgColor: 'bg-yellow-500'
    },
    {
      title: 'Preventive Maintenance',
      description: 'Proactive care programs',
      icon: <FaCogs size={18} />,
      link: '/services/preventive-maintenance',
      gradient: 'from-teal-500 to-cyan-700',
      bgColor: 'bg-teal-500'
    }
  ];

  const isActive = (href) => {
    if (href.startsWith('/')) {
      return location.pathname === href;
    }
    return false;
  };

  return (
    <nav className="sticky top-0 w-full bg-white/95 backdrop-blur-lg shadow-lg z-50 border-b border-gray-100">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-20">
          {/* Logo */}
          <div className="flex items-center">
            <Link to="/" className="flex items-center">
              <img
                src="/NextGen-logo.png"
                alt="Nextgen Limited Company Inc."
                className="h-14 w-auto"
              />
            </Link>
          </div>

          {/* Desktop Navigation */}
          <div className="hidden lg:flex items-center space-x-1">
            {/* Home Link */}
            <Link
              to="/"
              className={`px-4 py-2 font-semibold transition-colors duration-300 relative group ${isActive('/') ? 'text-blue-600' : 'text-gray-700 hover:text-blue-600'}`}
            >
              Home
              <span className={`absolute bottom-0 left-0 w-full h-0.5 bg-gradient-to-r from-blue-600 to-cyan-500 transform transition-transform duration-300 origin-left ${isActive('/') ? 'scale-x-100' : 'scale-x-0 group-hover:scale-x-100'}`}></span>
            </Link>

            {/* Our Services Mega Dropdown */}
            <div
              className="relative"
              onMouseEnter={() => setServicesOpen(true)}
              onMouseLeave={() => {
                setServicesOpen(false);
                setActiveSubmenu(null);
              }}
            >
              <button
                className={`px-4 py-2 font-semibold transition-colors duration-300 relative group flex items-center gap-1.5 ${location.pathname === '/services' || location.pathname === '/data-center' ? 'text-blue-600' : 'text-gray-700 hover:text-blue-600'}`}
              >
                Our Services
                <FaChevronDown size={11} className={`transition-transform duration-300 ${servicesOpen ? 'rotate-180' : ''}`} />
                <span className={`absolute bottom-0 left-0 w-full h-0.5 bg-gradient-to-r from-blue-600 to-cyan-500 transform transition-transform duration-300 origin-left ${location.pathname === '/services' || location.pathname === '/data-center' ? 'scale-x-100' : 'scale-x-0 group-hover:scale-x-100'}`}></span>
              </button>

              {/* Mega Dropdown Menu */}
              <div className={`absolute top-full left-1/2 -translate-x-1/2 mt-2 w-[700px] bg-white rounded-2xl shadow-2xl border border-gray-100 transition-all duration-300 ${servicesOpen ? 'opacity-100 visible translate-y-0' : 'opacity-0 invisible -translate-y-3'}`}>

                {/* Decorative top arrow */}
                <div className="absolute -top-2 left-1/2 -translate-x-1/2 w-4 h-4 bg-white border-l border-t border-gray-100 transform rotate-45"></div>

                <div className="p-4 relative">
                  {/* Header */}
                  <div className="mb-3 pb-2 border-b border-gray-100">
                    <h3 className="text-base font-bold text-gray-900">Explore Our Services</h3>
                    <p className="text-xs text-gray-500">Comprehensive IT & Telecom Solutions for Enterprise Excellence</p>
                  </div>

                  <div className="grid grid-cols-3 gap-4">

                    {/* Column 1: BUILD & CONNECT */}
                    <div className="space-y-1">
                      <div className="flex items-center gap-2 mb-2">
                        <div className="w-6 h-6 rounded-md bg-gradient-to-br from-blue-500 to-indigo-600 flex items-center justify-center">
                          <FaLayerGroup size={12} className="text-white" />
                        </div>
                        <p className="text-xs font-bold text-gray-800 uppercase tracking-wider">Build & Connect</p>
                      </div>
                      {buildConnectServices.map((service, index) => (
                        <Link
                          key={index}
                          to={service.link}
                          onClick={() => setServicesOpen(false)}
                          className="flex items-center gap-2 px-2 py-1.5 rounded-lg hover:bg-gradient-to-r hover:from-gray-50 hover:to-blue-50 transition-all duration-200 group"
                        >
                          <span className={`w-7 h-7 rounded-lg bg-gradient-to-br ${service.gradient} flex items-center justify-center text-white shadow-md group-hover:scale-105 transition-transform duration-200`}>
                            {service.icon}
                          </span>
                          <span className="font-medium text-gray-800 group-hover:text-blue-600 transition-colors text-sm">{service.title}</span>
                        </Link>
                      ))}

                      {/* DEPLOY NATIONWIDE */}
                      <div className="flex items-center gap-2 mb-2 mt-3 pt-3 border-t border-gray-100">
                        <div className="w-6 h-6 rounded-md bg-gradient-to-br from-orange-500 to-red-600 flex items-center justify-center">
                          <FaGlobe size={12} className="text-white" />
                        </div>
                        <p className="text-xs font-bold text-gray-800 uppercase tracking-wider">Deploy Nationwide</p>
                      </div>
                      {deployServices.map((service, index) => (
                        <div
                          key={index}
                          className="relative group/deploy"
                        >
                          <div
                            onClick={(e) => {
                              e.preventDefault();
                              e.stopPropagation();
                              setActiveSubmenu(activeSubmenu === service.submenuId ? null : service.submenuId);
                            }}
                            className={`flex items-center gap-2 px-2 py-1.5 rounded-lg hover:bg-gradient-to-r hover:from-gray-50 hover:to-orange-50 transition-all duration-200 group cursor-pointer ${activeSubmenu === service.submenuId ? 'bg-orange-50' : ''}`}
                          >
                            <span className={`w-7 h-7 rounded-lg bg-gradient-to-br ${service.gradient} flex items-center justify-center text-white shadow-md group-hover:scale-105 transition-transform duration-200`}>
                              {service.icon}
                            </span>
                            <span className={`font-medium group-hover:text-orange-600 transition-colors text-sm flex-1 ${activeSubmenu === service.submenuId ? 'text-orange-600' : 'text-gray-800'}`}>{service.title}</span>
                            <FaChevronRight size={10} className={`text-gray-400 group-hover:text-orange-500 transition-transform duration-200 ${activeSubmenu === service.submenuId ? 'rotate-90 text-orange-500' : ''}`} />
                          </div>

                          {/* RIGHT SIDE SUBMENU - Positioned upward */}
                          <div
                            className={`absolute left-full bottom-0 ml-3 w-56 bg-white rounded-xl shadow-2xl border border-gray-100 p-3 transition-all duration-200 z-50 ${activeSubmenu === service.submenuId ? 'opacity-100 visible translate-x-0' : 'opacity-0 invisible -translate-x-2 pointer-events-none'}`}
                          >
                            <div className="flex items-center gap-2 mb-2 pb-2 border-b border-gray-100">
                              <div className="w-4 h-4 rounded bg-gradient-to-br from-orange-500 to-red-600 flex items-center justify-center">
                                <FaRocket size={8} className="text-white" />
                              </div>
                              <p className="text-xs font-bold text-gray-700 uppercase tracking-wider">Rollout Services</p>
                            </div>
                            <div className="space-y-1">
                              {service.submenu.map((item, idx) => (
                                <Link
                                  key={idx}
                                  to={item.link}
                                  onClick={() => {
                                    setServicesOpen(false);
                                    setActiveSubmenu(null);
                                  }}
                                  className="flex items-center gap-2 px-2 py-1.5 rounded-lg hover:bg-orange-50 transition-all duration-200 group/item"
                                >
                                  <span className={`w-6 h-6 rounded-lg bg-gradient-to-br ${item.gradient} flex items-center justify-center text-white shadow-sm group-hover/item:scale-105 transition-transform duration-200`}>
                                    {item.icon}
                                  </span>
                                  <span className="font-medium text-gray-700 group-hover/item:text-orange-600 transition-colors text-xs">{item.title}</span>
                                </Link>
                              ))}
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>

                    {/* Column 2: PROTECT & SECURE + COMMUNICATE & SCALE */}
                    <div className="space-y-1">
                      <div className="flex items-center gap-2 mb-2">
                        <div className="w-6 h-6 rounded-md bg-gradient-to-br from-red-500 to-rose-700 flex items-center justify-center">
                          <FaShieldAlt size={12} className="text-white" />
                        </div>
                        <p className="text-xs font-bold text-gray-800 uppercase tracking-wider">Protect & Secure</p>
                      </div>
                      {protectServices.map((service, index) => (
                        <Link
                          key={index}
                          to={service.link}
                          onClick={() => setServicesOpen(false)}
                          className="flex items-center gap-2 px-2 py-1.5 rounded-lg hover:bg-gradient-to-r hover:from-gray-50 hover:to-red-50 transition-all duration-200 group"
                        >
                          <span className={`w-7 h-7 rounded-lg bg-gradient-to-br ${service.gradient} flex items-center justify-center text-white shadow-md group-hover:scale-105 transition-transform duration-200`}>
                            {service.icon}
                          </span>
                          <span className="font-medium text-gray-800 group-hover:text-red-600 transition-colors text-sm">{service.title}</span>
                        </Link>
                      ))}

                      {/* COMMUNICATE & SCALE */}
                      <div className="flex items-center gap-2 mb-2 mt-3 pt-3 border-t border-gray-100">
                        <div className="w-6 h-6 rounded-md bg-gradient-to-br from-violet-500 to-purple-700 flex items-center justify-center">
                          <FaSatelliteDish size={12} className="text-white" />
                        </div>
                        <p className="text-xs font-bold text-gray-800 uppercase tracking-wider">Communicate & Scale</p>
                      </div>
                      {communicateServices.map((service, index) => (
                        <Link
                          key={index}
                          to={service.link}
                          onClick={() => setServicesOpen(false)}
                          className="flex items-center gap-2 px-2 py-1.5 rounded-lg hover:bg-gradient-to-r hover:from-gray-50 hover:to-purple-50 transition-all duration-200 group"
                        >
                          <span className={`w-7 h-7 rounded-lg bg-gradient-to-br ${service.gradient} flex items-center justify-center text-white shadow-md group-hover:scale-105 transition-transform duration-200`}>
                            {service.icon}
                          </span>
                          <span className="font-medium text-gray-800 group-hover:text-purple-600 transition-colors text-sm">{service.title}</span>
                        </Link>
                      ))}
                    </div>

                    {/* Column 3: SUPPORT & MAINTAIN + CTA */}
                    <div className="space-y-1">
                      <div className="flex items-center gap-2 mb-2">
                        <div className="w-6 h-6 rounded-md bg-gradient-to-br from-green-500 to-emerald-600 flex items-center justify-center">
                          <FaHeadset size={12} className="text-white" />
                        </div>
                        <p className="text-xs font-bold text-gray-800 uppercase tracking-wider">Support & Maintain</p>
                      </div>
                      {supportServices.map((service, index) => (
                        <Link
                          key={index}
                          to={service.link}
                          onClick={() => setServicesOpen(false)}
                          className="flex items-center gap-2 px-2 py-1.5 rounded-lg hover:bg-gradient-to-r hover:from-gray-50 hover:to-green-50 transition-all duration-200 group"
                        >
                          <span className={`w-7 h-7 rounded-lg bg-gradient-to-br ${service.gradient} flex items-center justify-center text-white shadow-md group-hover:scale-105 transition-transform duration-200`}>
                            {service.icon}
                          </span>
                          <span className="font-medium text-gray-800 group-hover:text-green-600 transition-colors text-sm">{service.title}</span>
                        </Link>
                      ))}

                      {/* Quick Contact Card */}
                      <div className="mt-3 pt-3 border-t border-gray-100">
                        <div className="bg-gradient-to-br from-blue-600 to-cyan-500 rounded-lg p-3 text-white">
                          <div className="flex items-center gap-2 mb-1">
                            <FaHandshake size={14} />
                            <span className="font-bold text-sm">Need Custom Solutions?</span>
                          </div>
                          <p className="text-xs text-blue-100 mb-2">Let's discuss your requirements</p>
                          <Link
                            to="/contact"
                            className="block w-full py-1.5 bg-white text-blue-600 rounded-md font-semibold text-center text-xs hover:shadow-lg transition-all duration-200"
                          >
                            Contact Us
                          </Link>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Footer CTA */}
                  <div className="mt-3 pt-2 border-t border-gray-100 flex items-center justify-between">
                    <div>
                      <span className="text-sm font-semibold text-gray-700">Explore all our solutions</span>
                      <span className="text-xs text-gray-500 ml-2">115+ service points across North America</span>
                    </div>
                    <Link
                      to="/services"
                      className="flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-blue-600 to-cyan-500 text-white rounded-lg font-semibold hover:shadow-lg hover:shadow-blue-500/30 transition-all duration-300 text-sm"
                    >
                      View All Services
                      <FaChevronRight size={11} />
                    </Link>
                  </div>
                </div>
              </div>
            </div>

            {/* About Us Link */}
            <Link
              to="/about"
              className="px-4 py-2 text-gray-700 hover:text-blue-600 font-semibold transition-colors duration-300 relative group"
            >
              About Us
              <span className="absolute bottom-0 left-0 w-full h-0.5 bg-gradient-to-r from-blue-600 to-cyan-500 transform scale-x-0 group-hover:scale-x-100 transition-transform duration-300 origin-left"></span>
            </Link>

            {/* Contact Us Link */}
            <Link
              to="/contact"
              className={`px-4 py-2 font-semibold transition-colors duration-300 relative group ${isActive('/contact') ? 'text-blue-600' : 'text-gray-700 hover:text-blue-600'}`}
            >
              Contact Us
              <span className={`absolute bottom-0 left-0 w-full h-0.5 bg-gradient-to-r from-blue-600 to-cyan-500 transform transition-transform duration-300 origin-left ${isActive('/contact') ? 'scale-x-100' : 'scale-x-0 group-hover:scale-x-100'}`}></span>
            </Link>

            <Link
              to="/contact"
              className="ml-4 px-6 py-2.5 bg-gradient-to-r from-blue-600 to-cyan-500 text-white rounded-lg font-semibold hover:shadow-lg hover:shadow-blue-500/30 transition-all duration-300"
            >
              Get a Quote
            </Link>
          </div>

          {/* Mobile Menu Button */}
          <button
            onClick={() => setIsOpen(!isOpen)}
            className="lg:hidden w-10 h-10 flex items-center justify-center text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
          >
            {isOpen ? <FaTimes size={22} /> : <FaBars size={22} />}
          </button>
        </div>

        {/* Mobile Menu */}
        <div className={`lg:hidden transition-all duration-300 ease-in-out scrollbar-hide ${isOpen ? 'max-h-[calc(100vh-80px)] overflow-y-auto pb-6' : 'max-h-0 overflow-hidden'}`}>
          <div className="space-y-2">
            {/* Home */}
            <Link
              to="/"
              onClick={() => setIsOpen(false)}
              className={`block px-4 py-3 rounded-lg font-medium transition-colors ${isActive('/') ? 'text-blue-600 bg-blue-50' : 'text-gray-700 hover:text-blue-600 hover:bg-blue-50'}`}
            >
              Home
            </Link>

            {/* Our Services - Mobile Accordion */}
            <div>
              <button
                onClick={() => setMobileServicesOpen(!mobileServicesOpen)}
                className="w-full flex items-center justify-between px-4 py-3 rounded-lg font-medium text-gray-700 hover:text-blue-600 hover:bg-blue-50 transition-colors"
              >
                Our Services
                <FaChevronDown size={12} className={`transition-transform duration-300 ${mobileServicesOpen ? 'rotate-180' : ''}`} />
              </button>

              <div className={`transition-all duration-300 ${mobileServicesOpen ? 'max-h-none' : 'max-h-0 overflow-hidden'}`}>
                <div className="pl-2 space-y-1 mt-2">

                  {/* BUILD & CONNECT - Collapsible */}
                  <div className="border-b border-gray-100">
                    <button
                      onClick={() => setMobileCategoryOpen(mobileCategoryOpen === 'build' ? null : 'build')}
                      className="w-full flex items-center justify-between px-3 py-3 hover:bg-blue-50 rounded-lg transition-colors"
                    >
                      <div className="flex items-center gap-2">
                        <div className="w-6 h-6 rounded-md bg-gradient-to-br from-blue-500 to-indigo-600 flex items-center justify-center">
                          <FaLayerGroup size={11} className="text-white" />
                        </div>
                        <p className="text-xs font-bold text-gray-700 uppercase tracking-wider">Build & Connect</p>
                      </div>
                      <FaChevronDown size={10} className={`text-gray-400 transition-transform duration-300 ${mobileCategoryOpen === 'build' ? 'rotate-180' : ''}`} />
                    </button>
                    <div className={`overflow-hidden transition-all duration-300 ${mobileCategoryOpen === 'build' ? 'max-h-60' : 'max-h-0'}`}>
                      <div className="pb-2">
                        {buildConnectServices.map((service, index) => (
                          <Link
                            key={index}
                            to={service.link}
                            onClick={() => setIsOpen(false)}
                            className="flex items-center gap-3 px-3 py-2 ml-4 rounded-lg text-sm text-gray-600 hover:text-blue-600 hover:bg-blue-50 transition-colors"
                          >
                            <span className={`w-7 h-7 rounded-lg bg-gradient-to-br ${service.gradient} flex items-center justify-center text-white text-xs`}>
                              {service.icon}
                            </span>
                            {service.title}
                          </Link>
                        ))}
                      </div>
                    </div>
                  </div>

                  {/* PROTECT & SECURE - Collapsible */}
                  <div className="border-b border-gray-100">
                    <button
                      onClick={() => setMobileCategoryOpen(mobileCategoryOpen === 'protect' ? null : 'protect')}
                      className="w-full flex items-center justify-between px-3 py-3 hover:bg-red-50 rounded-lg transition-colors"
                    >
                      <div className="flex items-center gap-2">
                        <div className="w-6 h-6 rounded-md bg-gradient-to-br from-red-500 to-rose-700 flex items-center justify-center">
                          <FaShieldAlt size={11} className="text-white" />
                        </div>
                        <p className="text-xs font-bold text-gray-700 uppercase tracking-wider">Protect & Secure</p>
                      </div>
                      <FaChevronDown size={10} className={`text-gray-400 transition-transform duration-300 ${mobileCategoryOpen === 'protect' ? 'rotate-180' : ''}`} />
                    </button>
                    <div className={`overflow-hidden transition-all duration-300 ${mobileCategoryOpen === 'protect' ? 'max-h-60' : 'max-h-0'}`}>
                      <div className="pb-2">
                        {protectServices.map((service, index) => (
                          <Link
                            key={index}
                            to={service.link}
                            onClick={() => setIsOpen(false)}
                            className="flex items-center gap-3 px-3 py-2 ml-4 rounded-lg text-sm text-gray-600 hover:text-red-600 hover:bg-red-50 transition-colors"
                          >
                            <span className={`w-7 h-7 rounded-lg bg-gradient-to-br ${service.gradient} flex items-center justify-center text-white text-xs`}>
                              {service.icon}
                            </span>
                            {service.title}
                          </Link>
                        ))}
                      </div>
                    </div>
                  </div>

                  {/* COMMUNICATE & SCALE - Collapsible */}
                  <div className="border-b border-gray-100">
                    <button
                      onClick={() => setMobileCategoryOpen(mobileCategoryOpen === 'communicate' ? null : 'communicate')}
                      className="w-full flex items-center justify-between px-3 py-3 hover:bg-purple-50 rounded-lg transition-colors"
                    >
                      <div className="flex items-center gap-2">
                        <div className="w-6 h-6 rounded-md bg-gradient-to-br from-violet-500 to-purple-700 flex items-center justify-center">
                          <FaSatelliteDish size={11} className="text-white" />
                        </div>
                        <p className="text-xs font-bold text-gray-700 uppercase tracking-wider">Communicate & Scale</p>
                      </div>
                      <FaChevronDown size={10} className={`text-gray-400 transition-transform duration-300 ${mobileCategoryOpen === 'communicate' ? 'rotate-180' : ''}`} />
                    </button>
                    <div className={`overflow-hidden transition-all duration-300 ${mobileCategoryOpen === 'communicate' ? 'max-h-60' : 'max-h-0'}`}>
                      <div className="pb-2">
                        {communicateServices.map((service, index) => (
                          <Link
                            key={index}
                            to={service.link}
                            onClick={() => setIsOpen(false)}
                            className="flex items-center gap-3 px-3 py-2 ml-4 rounded-lg text-sm text-gray-600 hover:text-purple-600 hover:bg-purple-50 transition-colors"
                          >
                            <span className={`w-7 h-7 rounded-lg bg-gradient-to-br ${service.gradient} flex items-center justify-center text-white text-xs`}>
                              {service.icon}
                            </span>
                            {service.title}
                          </Link>
                        ))}
                      </div>
                    </div>
                  </div>

                  {/* DEPLOY NATIONWIDE - Collapsible */}
                  <div className="border-b border-gray-100">
                    <button
                      onClick={() => setMobileCategoryOpen(mobileCategoryOpen === 'deploy' ? null : 'deploy')}
                      className="w-full flex items-center justify-between px-3 py-3 hover:bg-orange-50 rounded-lg transition-colors"
                    >
                      <div className="flex items-center gap-2">
                        <div className="w-6 h-6 rounded-md bg-gradient-to-br from-orange-500 to-red-600 flex items-center justify-center">
                          <FaGlobe size={11} className="text-white" />
                        </div>
                        <p className="text-xs font-bold text-gray-700 uppercase tracking-wider">Deploy Nationwide</p>
                      </div>
                      <FaChevronDown size={10} className={`text-gray-400 transition-transform duration-300 ${mobileCategoryOpen === 'deploy' ? 'rotate-180' : ''}`} />
                    </button>
                    <div className={`overflow-hidden transition-all duration-300 ${mobileCategoryOpen === 'deploy' ? 'max-h-80' : 'max-h-0'}`}>
                      <div className="pb-2">
                        {deployServices.map((service, serviceIndex) => (
                          <div key={serviceIndex}>
                            <button
                              onClick={() => setMobileSubmenuOpen(mobileSubmenuOpen === service.submenuId ? null : service.submenuId)}
                              className="w-full flex items-center justify-between px-3 py-2 ml-4 rounded-lg text-sm text-gray-600 hover:text-orange-600 hover:bg-orange-50 transition-colors"
                            >
                              <div className="flex items-center gap-3">
                                <span className={`w-7 h-7 rounded-lg bg-gradient-to-br ${service.gradient} flex items-center justify-center text-white text-xs`}>
                                  {service.icon}
                                </span>
                                {service.title}
                              </div>
                              <FaChevronDown size={10} className={`transition-transform duration-300 ${mobileSubmenuOpen === service.submenuId ? 'rotate-180' : ''}`} />
                            </button>
                            <div className={`overflow-hidden transition-all duration-300 ${mobileSubmenuOpen === service.submenuId ? 'max-h-60' : 'max-h-0'}`}>
                              <div className="pl-12 space-y-1 mt-1 mb-2">
                                {service.submenu.map((item, idx) => (
                                  <Link
                                    key={idx}
                                    to={item.link}
                                    onClick={() => setIsOpen(false)}
                                    className="flex items-center gap-2 px-3 py-2 rounded-lg text-sm text-gray-500 hover:text-orange-600 hover:bg-orange-50 transition-colors"
                                  >
                                    <span className={`w-6 h-6 rounded bg-gradient-to-br ${item.gradient} flex items-center justify-center text-white`}>
                                      {item.icon}
                                    </span>
                                    {item.title}
                                  </Link>
                                ))}
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>

                  {/* SUPPORT & MAINTAIN - Collapsible */}
                  <div className="border-b border-gray-100">
                    <button
                      onClick={() => setMobileCategoryOpen(mobileCategoryOpen === 'support' ? null : 'support')}
                      className="w-full flex items-center justify-between px-3 py-3 hover:bg-green-50 rounded-lg transition-colors"
                    >
                      <div className="flex items-center gap-2">
                        <div className="w-6 h-6 rounded-md bg-gradient-to-br from-green-500 to-emerald-600 flex items-center justify-center">
                          <FaHeadset size={11} className="text-white" />
                        </div>
                        <p className="text-xs font-bold text-gray-700 uppercase tracking-wider">Support & Maintain</p>
                      </div>
                      <FaChevronDown size={10} className={`text-gray-400 transition-transform duration-300 ${mobileCategoryOpen === 'support' ? 'rotate-180' : ''}`} />
                    </button>
                    <div className={`overflow-hidden transition-all duration-300 ${mobileCategoryOpen === 'support' ? 'max-h-60' : 'max-h-0'}`}>
                      <div className="pb-2">
                        {supportServices.map((service, index) => (
                          <Link
                            key={index}
                            to={service.link}
                            onClick={() => setIsOpen(false)}
                            className="flex items-center gap-3 px-3 py-2 ml-4 rounded-lg text-sm text-gray-600 hover:text-green-600 hover:bg-green-50 transition-colors"
                          >
                            <span className={`w-7 h-7 rounded-lg bg-gradient-to-br ${service.gradient} flex items-center justify-center text-white text-xs`}>
                              {service.icon}
                            </span>
                            {service.title}
                          </Link>
                        ))}
                      </div>
                    </div>
                  </div>

                </div>
              </div>
            </div>

            {/* About Us */}
            <Link
              to="/about"
              onClick={() => setIsOpen(false)}
              className="block px-4 py-3 text-gray-700 hover:text-blue-600 hover:bg-blue-50 rounded-lg font-medium transition-colors"
            >
              About Us
            </Link>

            {/* Contact Us */}
            <Link
              to="/contact"
              onClick={() => {
                setIsOpen(false);
                window.scrollTo(0, 0);
              }}
              className="block px-4 py-3 text-gray-700 hover:text-blue-600 hover:bg-blue-50 rounded-lg font-medium transition-colors"
            >
              Contact Us
            </Link>

            <Link
              to="/contact"
              onClick={() => setIsOpen(false)}
              className="block px-4 py-3 bg-gradient-to-r from-blue-600 to-cyan-500 text-white rounded-lg font-semibold text-center mt-4"
            >
              Get a Quote
            </Link>
          </div>
        </div>
      </div>
    </nav>
  );
};

export default Navigation;
